'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type SnackbarVariant = 'success' | 'error' | 'info' | 'notification';

interface SnackbarMessage {
  id: string;
  message: string;
  variant: SnackbarVariant;
  duration?: number;
}

interface SnackbarContextType {
  showSnackbar: (message: string, variant: SnackbarVariant, duration?: number) => void;
  hideSnackbar: (id: string) => void;
  snackbars: SnackbarMessage[];
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([]);

  const showSnackbar = useCallback((message: string, variant: SnackbarVariant, duration: number = 5000) => {
    const id = Date.now().toString();
    const newSnackbar: SnackbarMessage = { id, message, variant, duration };
    
    setSnackbars(prev => [...prev, newSnackbar]);

    if (duration > 0) {
      setTimeout(() => {
        hideSnackbar(id);
      }, duration);
    }
  }, []);

  const hideSnackbar = useCallback((id: string) => {
    setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar, snackbars }}>
      {children}
    </SnackbarContext.Provider>
  );
};

// Convenience functions for common use cases
export const useSuccessSnackbar = () => {
  const { showSnackbar } = useSnackbar();
  return useCallback((message: string, duration?: number) => {
    showSnackbar(message, 'success', duration);
  }, [showSnackbar]);
};

export const useErrorSnackbar = () => {
  const { showSnackbar } = useSnackbar();
  return useCallback((message: string, duration?: number) => {
    showSnackbar(message, 'error', duration);
  }, [showSnackbar]);
};

export const useInfoSnackbar = () => {
  const { showSnackbar } = useSnackbar();
  return useCallback((message: string, duration?: number) => {
    showSnackbar(message, 'info', duration);
  }, [showSnackbar]);
};

export const useNotificationSnackbar = () => {
  const { showSnackbar } = useSnackbar();
  return useCallback((message: string, duration?: number) => {
    showSnackbar(message, 'notification', duration);
  }, [showSnackbar]);
};